export const ROADMAP_STORAGE_KEY = 'compito.learning-roadmaps.v1';

export const TOPIC_STATUSES = {
    NOT_STARTED: {label: 'Not started', symbol: '○', color: 'text-[var(--text-muted)]'},
    LEARNING: {label: 'Learning', symbol: '◐', color: 'text-amber-400'},
    PRACTICED: {label: 'Practiced', symbol: '●', color: 'text-[var(--brand-lavender)]'},
    COMFORTABLE: {label: 'Comfortable', symbol: '✓', color: 'text-emerald-400'},
};

const resource = (id, type, title, source) => ({id, type, title, source});
const exercise = (id, title, completed = false) => ({id, title, completed});
const recallCard = (id, question) => ({id, question});

export const initialRoadmaps = [
    {
        id: 'travel-video-making',
        title: 'Travel Video Making',
        emoji: '🎬',
        description: 'Build the camera, storytelling, and editing skills to create memorable travel films.',
        goal: 'Create a polished 3–5 minute travel film from planning to final color grade.',
        difficulty: 'Beginner to intermediate',
        estimatedHours: 28,
        modules: [
            {
                id: 'camera-fundamentals',
                title: 'Camera Fundamentals',
                description: 'Control light, motion, and color confidently in changing conditions.',
                order: 1,
                topics: [
                    {
                        id: 'exposure-triangle',
                        title: 'Exposure Triangle',
                        description: 'Understand how aperture, shutter speed, and ISO work together.',
                        status: 'COMFORTABLE',
                        order: 1,
                        estimatedMinutes: 60,
                        prerequisites: [],
                        resources: [resource('exposure-video', 'VIDEO', 'The exposure triangle in 8 minutes', 'YouTube')],
                        exercises: [exercise('exposure-exercise', 'Match the exposure of three differently lit scenes', true)],
                        recallCards: [recallCard('exposure-recall', 'What happens when one side of the exposure triangle changes?')],
                    },
                    {
                        id: 'aperture',
                        title: 'Aperture',
                        description: 'Use aperture to balance exposure and depth of field.',
                        status: 'COMFORTABLE',
                        order: 2,
                        estimatedMinutes: 40,
                        prerequisites: ['exposure-triangle'],
                        resources: [], exercises: [], recallCards: [],
                    },
                    {
                        id: 'iso',
                        title: 'ISO',
                        description: 'Choose ISO deliberately while managing image noise.',
                        status: 'COMFORTABLE',
                        order: 3,
                        estimatedMinutes: 35,
                        prerequisites: ['exposure-triangle'],
                        resources: [], exercises: [], recallCards: [],
                    },
                    {
                        id: 'shutter-speed',
                        title: 'Shutter Speed',
                        description: 'Understand how shutter speed affects motion.',
                        status: 'LEARNING',
                        order: 4,
                        estimatedMinutes: 45,
                        prerequisites: ['exposure-triangle'],
                        resources: [
                            resource('shutter-youtube', 'VIDEO', 'Shutter speed explained', 'YouTube'),
                            resource('camera-note', 'NOTE', 'Camera basics', 'Compito note'),
                        ],
                        exercises: [
                            exercise('shutter-speeds', 'Shoot the same scene at 1/50, 1/250, and 1/1000'),
                            exercise('motion-blur', 'Compare the amount and quality of motion blur'),
                        ],
                        recallCards: [recallCard('shutter-rule', 'What is the 180-degree shutter rule?')],
                    },
                    {
                        id: 'frame-rates', title: 'Frame Rates', description: 'Pick frame rates for natural motion and slow motion.',
                        status: 'NOT_STARTED', order: 5, estimatedMinutes: 40, prerequisites: ['shutter-speed'], resources: [], exercises: [], recallCards: [],
                    },
                    {
                        id: 'white-balance', title: 'White Balance', description: 'Keep colors natural across mixed lighting.',
                        status: 'NOT_STARTED', order: 6, estimatedMinutes: 35, prerequisites: ['exposure-triangle'], resources: [], exercises: [], recallCards: [],
                    },
                ],
            },
            {
                id: 'composition', title: 'Composition', description: 'Arrange each frame so the viewer knows where to look.', order: 2,
                topics: [
                    {id: 'rule-of-thirds', title: 'Rule of Thirds', description: 'Create balanced frames with intentional subject placement.', status: 'COMFORTABLE', order: 1, estimatedMinutes: 35, prerequisites: [], resources: [], exercises: [], recallCards: []},
                    {id: 'leading-lines', title: 'Leading Lines', description: 'Guide attention through the frame with natural lines.', status: 'COMFORTABLE', order: 2, estimatedMinutes: 35, prerequisites: ['rule-of-thirds'], resources: [], exercises: [], recallCards: []},
                    {id: 'framing', title: 'Framing', description: 'Use environmental elements to frame the subject.', status: 'NOT_STARTED', order: 3, estimatedMinutes: 30, prerequisites: [], resources: [], exercises: [], recallCards: []},
                    {id: 'depth', title: 'Depth', description: 'Build layered frames with foreground, subject, and background.', status: 'NOT_STARTED', order: 4, estimatedMinutes: 40, prerequisites: ['framing'], resources: [], exercises: [], recallCards: []},
                    {id: 'visual-balance', title: 'Visual Balance', description: 'Distribute visual weight with purpose.', status: 'NOT_STARTED', order: 5, estimatedMinutes: 30, prerequisites: ['rule-of-thirds'], resources: [], exercises: [], recallCards: []},
                ],
            },
            {
                id: 'cinematic-shooting', title: 'Cinematic Shooting', description: 'Capture coverage that cuts together into a complete scene.', order: 3,
                topics: [
                    {id: 'establishing-shots', title: 'Establishing Shots', description: 'Orient the viewer before moving into the action.', status: 'PRACTICED', order: 1, estimatedMinutes: 35, prerequisites: ['composition'], resources: [], exercises: [], recallCards: []},
                    {id: 'shot-sizes', title: 'Wide / Medium / Close-up', description: 'Build sequences from complementary shot sizes.', status: 'NOT_STARTED', order: 2, estimatedMinutes: 45, prerequisites: ['establishing-shots'], resources: [], exercises: [], recallCards: []},
                    {id: 'camera-movements', title: 'Camera Movements', description: 'Choose motivated pans, tilts, pushes, and reveals.', status: 'NOT_STARTED', order: 3, estimatedMinutes: 55, prerequisites: ['composition'], resources: [], exercises: [], recallCards: []},
                    {id: 'b-roll-sequences', title: 'B-roll Sequences', description: 'Film purposeful coverage instead of isolated pretty shots.', status: 'LEARNING', order: 4, estimatedMinutes: 60, prerequisites: ['shot-sizes'], resources: [], exercises: [], recallCards: []},
                ],
            },
            {
                id: 'storytelling', title: 'Storytelling', description: 'Turn a collection of clips into an experience with direction.', order: 4,
                topics: [
                    {id: 'story-arc', title: 'Story Arc', description: 'Give the journey a beginning, change, and resolution.', status: 'PRACTICED', order: 1, estimatedMinutes: 50, prerequisites: [], resources: [], exercises: [], recallCards: []},
                    {id: 'shot-sequencing', title: 'Shot Sequencing', description: 'Arrange shots so actions and ideas flow clearly.', status: 'NOT_STARTED', order: 2, estimatedMinutes: 50, prerequisites: ['story-arc', 'b-roll-sequences'], resources: [], exercises: [], recallCards: []},
                    {id: 'visual-transitions', title: 'Visual Transitions', description: 'Connect scenes using motion, shape, color, and sound.', status: 'NOT_STARTED', order: 3, estimatedMinutes: 45, prerequisites: ['shot-sequencing'], resources: [], exercises: [], recallCards: []},
                ],
            },
            {
                id: 'editing', title: 'Editing', description: 'Shape raw footage into a finished film with rhythm and feeling.', order: 5,
                topics: [
                    {id: 'selection', title: 'Selection', description: 'Find the takes that best serve the story.', status: 'NOT_STARTED', order: 1, estimatedMinutes: 50, prerequisites: ['shot-sequencing'], resources: [], exercises: [], recallCards: []},
                    {id: 'rhythm-pacing', title: 'Rhythm & Pacing', description: 'Use cut timing to control energy and attention.', status: 'NOT_STARTED', order: 2, estimatedMinutes: 60, prerequisites: ['selection'], resources: [], exercises: [], recallCards: []},
                    {id: 'music', title: 'Music', description: 'Select and edit music around story beats.', status: 'NOT_STARTED', order: 3, estimatedMinutes: 45, prerequisites: ['story-arc'], resources: [], exercises: [], recallCards: []},
                    {id: 'sound-design', title: 'Sound Design', description: 'Build an immersive world beyond the camera audio.', status: 'NOT_STARTED', order: 4, estimatedMinutes: 65, prerequisites: ['music'], resources: [], exercises: [], recallCards: []},
                    {id: 'color-grading', title: 'Color Grading', description: 'Create a consistent look that supports the mood.', status: 'NOT_STARTED', order: 5, estimatedMinutes: 70, prerequisites: ['white-balance', 'selection'], resources: [], exercises: [], recallCards: []},
                ],
            },
        ],
    },
];

export function createId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `item-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getAllTopics(roadmap) {
    return (roadmap?.modules ?? []).flatMap((module) => module.topics ?? []);
}

export function getProgress(items = []) {
    if (items.length === 0) return {started: 0, comfortable: 0, total: 0, percentage: 0};
    const started = items.filter((item) => item.status !== 'NOT_STARTED').length;
    const comfortable = items.filter((item) => item.status === 'COMFORTABLE').length;
    return {started, comfortable, total: items.length, percentage: Math.round((started / items.length) * 100)};
}

export function findTopic(roadmap, topicId) {
    for (const module of roadmap?.modules ?? []) {
        const topic = module.topics?.find((item) => item.id === topicId);
        if (topic) return {topic, module};
    }
    return null;
}

export function loadRoadmaps() {
    try {
        const stored = window.localStorage.getItem(ROADMAP_STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        }
    } catch {
        // Fall back to the starter roadmap if local storage is unavailable or malformed.
    }
    return initialRoadmaps;
}
